import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import * as DocumentPicker from 'expo-document-picker';
import { AppLayout } from '../../components/layout/AppLayout';
import { supabase } from '../../api/supabase';
import theme from '../../theme';

interface ImportProgress {
  phase: 'idle' | 'uploading' | 'processing' | 'complete' | 'error';
  currentStep: number;
  totalSteps: number;
  message: string;
  details?: string;
  processed?: number;
  total?: number;
}

interface ImportStats {
  totalRecords: number;
  captainIdMatches: number;
  nameMatches: number;
  notFound: number;
  duplicates: number;
  totalAmount: number;
  accuracy: number;
  dateRange?: string;
  batchId?: string;
  sourceFile?: string;
  importType?: string;
  fileHash?: string;
  processedRecords?: number;
  rawRecordsSaved?: number;
}

interface DuplicateRecord {
  captainName: string;
  captainId: string;
  existingDate: string;
  newDate: string;
  amount: number;
}

interface ImportResult {
  success: boolean;
  message: string;
  stats?: ImportStats;
  duplicates?: DuplicateRecord[];
  errors?: string[];
}

export const CaremImportScreen = () => {
  const navigation = useNavigation();
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [progress, setProgress] = useState<ImportProgress>({
    phase: 'idle',
    currentStep: 0,
    totalSteps: 5,
    message: 'Ready to import Careem data',
  });
  const [importStats, setImportStats] = useState<ImportStats | null>(null);
  const [duplicateRecords, setDuplicateRecords] = useState<DuplicateRecord[]>([]);
  const [showDuplicates, setShowDuplicates] = useState(false);

  const steps = [
    { id: 1, name: 'File Upload', description: 'Upload CSV file from Careem' },
    { id: 2, name: 'File Validation', description: 'Validate file format and structure' },
    { id: 3, name: 'Duplicate Detection', description: 'Check for duplicate transactions by date' },
    { id: 4, name: 'Captain ID Matching', description: 'Match drivers using Careem Captain IDs' },
    { id: 5, name: 'Data Processing', description: 'Process captain earnings and commissions' },
    { id: 6, name: 'Database Update', description: 'Save data to accounting system' },
  ];

  const pickFile = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: 'text/csv',
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets[0]) {
        setSelectedFile(result.assets[0]);
        setProgress({
          phase: 'idle',
          currentStep: 0,
          totalSteps: 5,
          message: `Selected: ${result.assets[0].name}`,
        });
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to pick file');
      console.error('File picker error:', error);
    }
  };

  const performRealImport = async (skipDuplicateCheck = false) => {
    if (!selectedFile) {
      Alert.alert('Error', 'Please select a file first');
      return;
    }

    try {
      setProgress({ phase: 'uploading', currentStep: 1, totalSteps: 5, message: 'Reading file...' });
      
      // Read the file content using web-compatible method
      let content: string;
      if (selectedFile.uri) {
        // For web, we use fetch to read the blob
        const response = await fetch(selectedFile.uri);
        content = await response.text();
      } else {
        // Fallback: if we have a File object directly
        content = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.onerror = reject;
          reader.readAsText(selectedFile as any);
        });
      }
      
      setProgress({ 
        phase: 'processing', 
        currentStep: 2, 
        totalSteps: 5, 
        message: 'Validating file format...',
        details: 'Checking CSV structure and Careem headers'
      });

      // Parse CSV
      const lines = content.split('\n');
      if (lines.length < 2) {
        throw new Error('File appears to be empty or invalid');
      }

      const headers = lines[0].split(',');
      console.log('CSV Headers:', headers);

      if (!skipDuplicateCheck) {
        setProgress({ 
          phase: 'processing', 
          currentStep: 3, 
          totalSteps: 5, 
          message: 'Checking for duplicate data...',
          details: 'Verifying transaction dates against existing records (checking first 100 records)'
        });

        // Check for duplicates with timeout
        const duplicateCheck = await Promise.race([
          checkForDuplicates(lines),
          new Promise(resolve => setTimeout(() => resolve({ hasDuplicates: false, duplicateCount: 0, timeout: true }), 10000))
        ]) as any;
        
        if (duplicateCheck.timeout) {
          Alert.alert(
            'Duplicate Check Timeout',
            'Duplicate checking is taking too long. Would you like to skip duplicate checking and proceed with import?',
            [
              { text: 'Cancel', style: 'cancel', onPress: () => resetImport() },
              { text: 'Skip & Continue', onPress: () => processImport(lines) }
            ]
          );
          return;
        }
        
        if (duplicateCheck.hasDuplicates) {
          // Handle different types of duplicates
          if (duplicateCheck.isDuplicateFile) {
            Alert.alert(
              'Exact File Duplicate Detected!',
              `This exact file was already imported on ${new Date(duplicateCheck.duplicateFileInfo.importDate).toLocaleDateString()}\n\nFile: ${duplicateCheck.duplicateFileInfo.existingFile}\nRecords: ${duplicateCheck.duplicateCount}\nBatch ID: ${duplicateCheck.duplicateFileInfo.batchId.substring(0, 8)}`,
              [
                { text: 'Cancel', style: 'cancel', onPress: () => resetImport() },
                { text: 'Force Re-import', onPress: () => processImport(lines, { forceReimport: true }) },
                ...(duplicateCheck.selectiveImportOption?.canSelectiveImport ? [
                  { text: 'Selective Import', onPress: () => processImport(lines, { selectiveImport: true, duplicateCheck }) }
                ] : [])
              ]
            );
          } else if (duplicateCheck.isDateRangeOverlap && duplicateCheck.selectiveImportOption?.canSelectiveImport) {
            Alert.alert(
              'Date Range Overlap with Selective Import Option',
              `Period overlap detected: ${duplicateCheck.overlapDetails}\n\nAnalysis:\n• ${duplicateCheck.selectiveImportOption.newCaptains} new captains found\n• ${duplicateCheck.selectiveImportOption.duplicateCaptains} existing captains\n\nWould you like to import only the new captains?`,
              [
                { text: 'Cancel', style: 'cancel', onPress: () => resetImport() },
                { text: 'Import All', onPress: () => processImport(lines) },
                { text: 'Import New Only', onPress: () => processImport(lines, { selectiveImport: true, duplicateCheck }) }
              ]
            );
          } else {
            Alert.alert(
              'Duplicate Data Detected',
              `Found ${duplicateCheck.duplicateCount} potential duplicate records. ${duplicateCheck.isDateRangeOverlap ? `\nOverlap details: ${duplicateCheck.overlapDetails}` : 'Based on amount matching within date range.'}`,
              [
                { text: 'Cancel', style: 'cancel', onPress: () => resetImport() },
                { text: 'Continue', onPress: () => processImport(lines) }
              ]
            );
          }
          return;
        }
      }

      await processImport(lines);

    } catch (error) {
      console.error('Import error:', error);
      setProgress({
        phase: 'error',
        currentStep: 0,
        totalSteps: 5,
        message: 'Import failed',
        details: error.message || 'An unexpected error occurred',
        errors: [error.message || 'Unknown error']
      });
    }
  };

  const checkForDuplicates = async (lines: string[]) => {
    try {
      // Extract date range from filename or use default
      const dateRange = extractDateRangeFromFilename(selectedFile?.name || '');
      
      // Calculate file hash for exact duplicate detection
      const fileContent = lines.join('\n');
      const fileHash = await calculateFileHash(fileContent);
      
      // Level 1: Check for exact file duplicate using hash
      const { data: fileCheck, error: fileError } = await supabase
        .rpc('check_careem_file_duplicate', {
          p_file_hash: fileHash,
          p_start_date: dateRange.startDate,
          p_end_date: dateRange.endDate
        });

      if (fileError) {
        console.log('File duplicate check failed, proceeding with basic checks');
      } else if (fileCheck && fileCheck.length > 0 && fileCheck[0].is_duplicate) {
        return {
          hasDuplicates: true,
          duplicateCount: fileCheck[0].record_count,
          isDuplicateFile: true,
          duplicateFileInfo: {
            existingFile: fileCheck[0].existing_file,
            importDate: fileCheck[0].import_date,
            batchId: fileCheck[0].existing_batch_id
          }
        };
      }

      // Level 2: Check for date range overlaps (different files, same period)
      const { data: overlapCheck, error: overlapError } = await supabase
        .rpc('check_date_range_overlap', {
          p_category: 'careem',
          p_start_date: dateRange.startDate,
          p_end_date: dateRange.endDate
        });

      if (overlapError) {
        console.error('Error checking date overlap:', overlapError);
      } else if (overlapCheck && overlapCheck.length > 0 && overlapCheck[0].overlap_count > 0) {
        
        // Level 3: Analyze individual captains for selective import
        const captainIds = lines.slice(1).map(line => {
          const cells = line.split(',');
          return cells[0]?.trim();
        }).filter(id => id && id !== '');

        const { data: captainAnalysis, error: captainError } = await supabase
          .rpc('find_new_careem_captains', {
            p_captain_ids: captainIds,
            p_start_date: dateRange.startDate,
            p_end_date: dateRange.endDate
          });

        const newCaptains = captainAnalysis?.filter(c => c.recommendation.includes('NEW')) || [];
        const duplicateCaptains = captainAnalysis?.filter(c => c.recommendation.includes('DUPLICATE')) || [];

        return {
          hasDuplicates: true,
          duplicateCount: overlapCheck[0].overlap_count,
          isDateRangeOverlap: true,
          overlapDetails: overlapCheck[0].overlap_details,
          selectiveImportOption: {
            newCaptains: newCaptains.length,
            duplicateCaptains: duplicateCaptains.length,
            newCaptainsList: newCaptains.map(c => c.new_captain_id),
            canSelectiveImport: newCaptains.length > 0
          }
        };
      }

      // Level 4: Basic amount-based duplicate checking (fallback)
      const { data: existingTransactions, error } = await supabase
        .from('accounting_transactions')
        .select('transaction_date, amount, driver_id')
        .eq('category', 'careem')
        .gte('transaction_date', dateRange.startDate)
        .lte('transaction_date', dateRange.endDate);

      if (error) {
        console.error('Error fetching existing transactions:', error);
        return { hasDuplicates: false, duplicateCount: 0 };
      }

      let duplicateCount = 0;
      const existingDateAmounts = new Set();
      
      if (existingTransactions && existingTransactions.length > 0) {
        existingTransactions.forEach(tx => {
          existingDateAmounts.add(`${tx.transaction_date}-${tx.amount}`);
        });
      }

      // Check first 100 records for amount-based duplicates
      const recordsToCheck = Math.min(lines.length - 1, 100);
      
      for (let i = 1; i <= recordsToCheck; i++) {
        const line = lines[i]?.trim();
        if (!line) continue;

        const cells = line.split(',');
        const earnings = parseFloat(cells[7] || '0');
        
        if (earnings > 0) {
          const transactionDate = calculateTransactionDate(dateRange.startDate, dateRange.endDate, i, recordsToCheck);
          const checkKey = `${transactionDate}-${earnings}`;
          
          if (existingDateAmounts.has(checkKey)) {
            duplicateCount++;
          }
        }
      }

      return {
        hasDuplicates: duplicateCount > 0,
        duplicateCount,
        isDateRangeOverlap: false,
        dateRange,
        fileHash
      };
    } catch (error) {
      console.error('Error checking duplicates:', error);
      return { hasDuplicates: false, duplicateCount: 0 };
    }
  };

  // Helper function to calculate file hash
  const calculateFileHash = async (content: string): Promise<string> => {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(content);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      console.error('Error calculating file hash:', error);
      return 'hash_calculation_failed_' + Date.now();
    }
  };

  // Helper function to extract date range from filename
  const extractDateRangeFromFilename = (filename: string) => {
    // Try to extract dates from common filename patterns
    // e.g., "careem_2024-07-01_to_2024-07-07.csv"
    const datePattern = /(\d{4}-\d{2}-\d{2})/g;
    const dates = filename.match(datePattern);
    
    if (dates && dates.length >= 2) {
      return {
        startDate: dates[0],
        endDate: dates[dates.length - 1]
      };
    }
    
    // Try week patterns like "week_28_2024"
    const weekPattern = /week[_\s](\d+)[_\s](\d{4})/i;
    const weekMatch = filename.match(weekPattern);
    
    if (weekMatch) {
      const week = parseInt(weekMatch[1]);
      const year = parseInt(weekMatch[2]);
      const startDate = getDateFromWeek(week, year);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      
      return {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
      };
    }
    
    // Default to current week if no pattern found
    const today = new Date();
    const startOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    return {
      startDate: startOfWeek.toISOString().split('T')[0],
      endDate: endOfWeek.toISOString().split('T')[0]
    };
  };

  // Helper function to get date from week number
  const getDateFromWeek = (week: number, year: number) => {
    const firstDayOfYear = new Date(year, 0, 1);
    const daysOffset = (week - 1) * 7;
    const firstDayOfWeek = firstDayOfYear.getDay();
    const daysToAdd = daysOffset - firstDayOfWeek + 1;
    
    const result = new Date(year, 0, 1 + daysToAdd);
    return result;
  };

  // Helper function to calculate transaction date within range
  const calculateTransactionDate = (startDate: string, endDate: string, recordIndex: number, totalRecords: number) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const totalDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    
    // Distribute records evenly across the date range
    const dayOffset = Math.floor((recordIndex / totalRecords) * totalDays);
    const transactionDate = new Date(start);
    transactionDate.setDate(start.getDate() + dayOffset);
    
    return transactionDate.toISOString().split('T')[0];
  };

  const processImport = async (lines: string[], options: any = {}) => {
    setProgress({ 
      phase: 'processing', 
      currentStep: 4, 
      totalSteps: 5, 
      message: 'Matching captains using Captain IDs...',
      details: 'Comparing Captain IDs with database'
    });

    // Extract date range for this import batch
    const dateRange = extractDateRangeFromFilename(selectedFile?.name || '');
    const batchId = crypto.randomUUID();
    const fileContent = lines.join('\n');
    const fileHash = await calculateFileHash(fileContent);

    let totalEarnings = 0;
    let processedCount = 0;
    let idMatches = 0;
    let nameMatches = 0;
    let notFoundCount = 0;
    const errors: string[] = [];
    const transactionsToInsert: any[] = [];
    const rawRecordsToInsert: any[] = [];

    // Filter captains for selective import if requested
    let captainsToProcess = lines.slice(1);
    if (options.selectiveImport && options.duplicateCheck?.selectiveImportOption?.newCaptainsList) {
      const newCaptainIds = options.duplicateCheck.selectiveImportOption.newCaptainsList;
      captainsToProcess = lines.slice(1).filter(line => {
        const captainId = line.split(',')[0]?.trim();
        return newCaptainIds.includes(captainId);
      });
      
      setProgress({ 
        phase: 'processing', 
        currentStep: 4, 
        totalSteps: 5, 
        message: 'Selective Import: Processing new captains only...',
        details: `Processing ${captainsToProcess.length} new captains out of ${lines.length - 1} total`
      });
    }

    // Process each data row
    for (let i = 0; i < captainsToProcess.length; i++) {
      const line = captainsToProcess[i].trim();
      if (!line) continue;

      const cells = line.split(',');
      const captainId = cells[0] || '';
      const captainName = cells[1] || '';
      const earnings = parseFloat(cells[7] || '0');

      if (earnings > 0 && captainName !== '') {
        try {
          const result = await findCareemDriverById(captainId, captainName);
          
          // Calculate transaction date within the range
          const transactionDate = calculateTransactionDate(dateRange.startDate, dateRange.endDate, i, captainsToProcess.length);
          
          // Add to raw records first
          rawRecordsToInsert.push({
            careem_captain_id: captainId,
            captain_name: captainName,
            driver_name: captainName,
            total_earnings: earnings,
            processed_to_accounting: result.driver ? true : false,
            driver_id: result.driver?.id || null,
            match_method: result.matchType || 'not_found',
            import_batch_id: batchId,
            batch_start_date: dateRange.startDate,
            batch_end_date: dateRange.endDate,
            source_file: selectedFile?.name || 'unknown_file',
            file_hash: fileHash,
            is_duplicate: options.forceReimport || false,
            selective_import: options.selectiveImport || false,
            records_in_batch: captainsToProcess.length
          });
          
          if (result.driver) {
            // Track match types
            if (result.matchType === 'id') {
              idMatches++;
            } else if (result.matchType === 'name') {
              nameMatches++;
            }
            
            // Prepare transaction for batch insert
            transactionsToInsert.push({
              transaction_date: transactionDate,
              type: 'earning',
              category: 'careem',
              amount: earnings,
              description: `Careem captain earnings: ${captainName}`,
              driver_id: result.driver.id,
              import_batch_id: batchId,
              batch_start_date: dateRange.startDate,
              batch_end_date: dateRange.endDate,
              source_filename: selectedFile?.name || 'unknown_file',
              records_in_batch: captainsToProcess.length,
              created_at: new Date().toISOString()
            });

            totalEarnings += earnings;
            processedCount++;
          } else {
            notFoundCount++;
            errors.push(`Driver not found: ${captainName} (ID: ${captainId})`);
          }
        } catch (error) {
          errors.push(`Error processing ${captainName}: ${error.message}`);
        }
      }

      // Update progress
      if (i % 25 === 0) {
        setProgress({ 
          phase: 'processing', 
          currentStep: 4, 
          totalSteps: 5, 
          message: options.selectiveImport ? 'Selective import in progress...' : 'Processing captain earnings...',
          details: `Processed ${i} of ${captainsToProcess.length} records`,
          processed: i,
          total: captainsToProcess.length
        });
      }
    }

    // Batch insert raw records first
    if (rawRecordsToInsert.length > 0) {
      setProgress({ 
        phase: 'processing', 
        currentStep: 5, 
        totalSteps: 5, 
        message: 'Saving raw import data...',
        details: `Inserting ${rawRecordsToInsert.length} raw records`
      });

      const { error: rawInsertError } = await supabase
        .from('careem_income_raw')
        .insert(rawRecordsToInsert);

      if (rawInsertError) {
        errors.push(`Failed to save raw data: ${rawInsertError.message}`);
      }
    }

    // Batch insert accounting transactions
    if (transactionsToInsert.length > 0) {
      setProgress({ 
        phase: 'processing', 
        currentStep: 5, 
        totalSteps: 5, 
        message: 'Saving accounting transactions...',
        details: `Inserting ${transactionsToInsert.length} transactions as batch ${batchId.substring(0, 8)}`
      });

      const { error: insertError } = await supabase
        .from('accounting_transactions')
        .insert(transactionsToInsert);

      if (insertError) {
        errors.push(`Failed to save transactions: ${insertError.message}`);
      }
    }

    setProgress({ 
      phase: 'processing', 
      currentStep: 5, 
      totalSteps: 5, 
      message: 'Finalizing import...',
      details: 'Calculating final results'
    });

    // Set final results
    const stats: ImportStats = {
      totalRecords: lines.length - 1,
      captainIdMatches: idMatches,
      nameMatches: nameMatches,
      notFound: notFoundCount,
      totalAmount: totalEarnings,
      accuracy: Math.round(((idMatches + nameMatches) / (captainsToProcess.length || 1)) * 100),
      dateRange: `${dateRange.startDate} to ${dateRange.endDate}`,
      batchId: batchId.substring(0, 8),
      sourceFile: selectedFile?.name || 'unknown',
      importType: options.selectiveImport ? 'Selective (New Captains Only)' : 'Full Import',
      fileHash: fileHash.substring(0, 16),
      processedRecords: captainsToProcess.length,
      rawRecordsSaved: rawRecordsToInsert.length
    };

    setImportStats(stats);
    setProgress({ 
      phase: 'complete', 
      currentStep: 5, 
      totalSteps: 5, 
      message: `${stats.importType} completed successfully!`,
      details: `${stats.captainIdMatches + stats.nameMatches} transactions imported for period ${stats.dateRange}${options.selectiveImport ? ` (${stats.processedRecords} new captains)` : ''}`,
      errors: errors.length > 0 ? errors : undefined
    });
  };

  const findCareemDriverById = async (captainId: string, captainName: string) => {
    try {
      // First try Captain ID match
      if (captainId && captainId.trim() !== '') {
        const { data: driverById, error: idError } = await supabase
          .from('drivers')
          .select('id, name, careem_captain_id')
          .eq('careem_captain_id', captainId.trim());
        
        if (driverById && driverById.length > 0 && !idError) {
          return { driver: driverById[0], matchType: 'id' };
        }
      }
      
      // Fallback to name matching
      if (captainName && captainName.trim() !== '') {
        const { data: driversByName, error: nameError } = await supabase
          .from('drivers')
          .select('id, name, careem_captain_id')
          .ilike('name', `%${captainName.trim()}%`);
        
        if (driversByName && driversByName.length > 0 && !nameError) {
          return { driver: driversByName[0], matchType: 'name' };
        }
      }
      
      return { driver: null, matchType: 'none' };
    } catch (error) {
      console.error(`Error finding Careem driver ${captainName}:`, error);
      return { driver: null, matchType: 'error' };
    }
  };

  const resetImport = () => {
    setSelectedFile(null);
    setProgress({
      phase: 'idle',
      currentStep: 0,
      totalSteps: 5,
      message: 'Ready to import Careem data',
    });
    setImportStats(null);
  };

  const getStepStatus = (stepNumber: number) => {
    if (progress.currentStep > stepNumber) return 'complete';
    if (progress.currentStep === stepNumber) return 'active';
    return 'pending';
  };

  const getStepColor = (status: string) => {
    switch (status) {
      case 'complete': return theme.colors.success;
      case 'active': return '#00C851'; // Careem green
      default: return theme.colors.gray400;
    }
  };

  const StepIndicator = ({ step, status }: { step: any; status: string }) => (
    <View style={styles.stepItem}>
      <View style={[styles.stepCircle, { borderColor: getStepColor(status) }]}>
        {status === 'complete' ? (
          <Ionicons name="checkmark" size={16} color={theme.colors.success} />
        ) : status === 'active' ? (
          <ActivityIndicator size="small" color="#00C851" />
        ) : (
          <Text style={[styles.stepNumber, { color: getStepColor(status) }]}>{step.id}</Text>
        )}
      </View>
      <View style={styles.stepInfo}>
        <Text style={[styles.stepName, { color: getStepColor(status) }]}>{step.name}</Text>
        <Text style={styles.stepDescription}>{step.description}</Text>
      </View>
    </View>
  );

  return (
    <AppLayout title="Careem Import" showBackButton onBackPress={() => navigation.goBack()}>
      <SafeAreaView style={styles.container}>
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
          {/* Header */}
          <View style={styles.header}>
            <View style={[styles.iconContainer, { backgroundColor: '#00C851' + '15' }]}>
              <Ionicons name="car-sport" size={24} color="#00C851" />
            </View>
            <View style={styles.headerInfo}>
              <Text style={styles.title}>Careem Data Import</Text>
              <Text style={styles.subtitle}>Import captain earnings and commission data from Careem CSV</Text>
            </View>
          </View>

          {/* File Selection */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>1. Select File</Text>
            <TouchableOpacity style={[styles.filePickerButton, { borderColor: '#00C851' }]} onPress={pickFile}>
              <Ionicons name="cloud-upload" size={20} color="#00C851" />
              <Text style={[styles.filePickerText, { color: '#00C851' }]}>
                {selectedFile ? selectedFile.name : 'Choose Careem CSV file'}
              </Text>
            </TouchableOpacity>
            {selectedFile && (
              <View style={styles.fileInfo}>
                <Ionicons name="document-text" size={16} color={theme.colors.success} />
                <Text style={styles.fileInfoText}>
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                </Text>
              </View>
            )}
          </View>

          {/* Progress Steps */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>2. Import Progress</Text>
            <View style={styles.stepsContainer}>
              {steps.map((step) => (
                <StepIndicator 
                  key={step.id} 
                  step={step} 
                  status={getStepStatus(step.id)} 
                />
              ))}
            </View>

            {progress.phase !== 'idle' && (
              <View style={[styles.progressCard, { borderLeftColor: '#00C851' }]}>
                <View style={styles.progressHeader}>
                  <Text style={styles.progressMessage}>{progress.message}</Text>
                  {progress.phase === 'processing' && (
                    <ActivityIndicator size="small" color="#00C851" />
                  )}
                </View>
                {progress.details && (
                  <Text style={styles.progressDetails}>{progress.details}</Text>
                )}
                {progress.processed && progress.total && (
                  <View style={styles.progressBar}>
                    <View 
                      style={[
                        styles.progressFill, 
                        { 
                          width: `${(progress.processed / progress.total) * 100}%`,
                          backgroundColor: '#00C851'
                        }
                      ]} 
                    />
                  </View>
                )}
              </View>
            )}
          </View>

          {/* Import Statistics */}
          {importStats && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>3. Import Results</Text>
              <View style={styles.statsContainer}>
                <View style={styles.statCard}>
                  <Text style={styles.statNumber}>{importStats.totalRecords}</Text>
                  <Text style={styles.statLabel}>Total Records</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: '#00C851' }]}>
                    {importStats.captainIdMatches}
                  </Text>
                  <Text style={styles.statLabel}>Captain ID Matches</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: theme.colors.warning }]}>
                    {importStats.nameMatches}
                  </Text>
                  <Text style={styles.statLabel}>Name Matches</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: theme.colors.error }]}>
                    {importStats.notFound}
                  </Text>
                  <Text style={styles.statLabel}>Not Found</Text>
                </View>
              </View>

              <View style={styles.summaryCard}>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Total Amount:</Text>
                  <Text style={styles.summaryValue}>
                    AED {importStats.totalAmount.toLocaleString()}
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Accuracy:</Text>
                  <Text style={[styles.summaryValue, { color: '#00C851' }]}>
                    {importStats.accuracy}%
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Import Date:</Text>
                  <Text style={styles.summaryValue}>
                    {new Date().toLocaleDateString()}
                  </Text>
                </View>
              </View>

              {/* Careem-specific insights */}
              <View style={styles.insightCard}>
                <View style={styles.insightHeader}>
                  <Ionicons name="analytics" size={20} color="#00C851" />
                  <Text style={styles.insightTitle}>Careem Insights</Text>
                </View>
                <Text style={styles.insightText}>
                  • Captain ID matching achieved {((importStats.captainIdMatches / importStats.totalRecords) * 100).toFixed(1)}% precision
                </Text>
                <Text style={styles.insightText}>
                  • Only {importStats.notFound} captain(s) require manual verification
                </Text>
                <Text style={styles.insightText}>
                  • Commission data successfully synchronized with accounting system
                </Text>
              </View>
            </View>
          )}

          {/* Action Buttons */}
          <View style={styles.actionContainer}>
            {progress.phase === 'idle' || progress.phase === 'complete' ? (
              <>
                <TouchableOpacity 
                  style={[
                    styles.primaryButton, 
                    { backgroundColor: '#00C851' },
                    !selectedFile && styles.disabledButton
                  ]} 
                  onPress={() => performRealImport(false)}
                  disabled={!selectedFile || progress.phase === 'processing'}
                >
                  <Ionicons name="cloud-upload" size={20} color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>
                    {progress.phase === 'complete' ? 'Import Another File' : 'Import with Duplicate Check'}
                  </Text>
                </TouchableOpacity>
                
                <TouchableOpacity 
                  style={[
                    styles.primaryButton, 
                    { backgroundColor: '#FF8800', marginTop: 8 },
                    !selectedFile && styles.disabledButton
                  ]} 
                  onPress={() => performRealImport(true)}
                  disabled={!selectedFile || progress.phase === 'processing'}
                >
                  <Ionicons name="flash" size={20} color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>Quick Import (Skip Duplicate Check)</Text>
                </TouchableOpacity>
                
                {progress.phase === 'complete' && (
                  <TouchableOpacity 
                    style={[styles.secondaryButton, { borderColor: '#00C851' }]} 
                    onPress={resetImport}
                  >
                    <Ionicons name="refresh" size={20} color="#00C851" />
                    <Text style={[styles.secondaryButtonText, { color: '#00C851' }]}>Reset</Text>
                  </TouchableOpacity>
                )}
              </>
            ) : (
              <TouchableOpacity style={styles.disabledButton} disabled>
                <ActivityIndicator size="small" color={theme.colors.white} />
                <Text style={styles.primaryButtonText}>Processing...</Text>
              </TouchableOpacity>
            )}
          </View>
        </ScrollView>
      </SafeAreaView>
    </AppLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  scrollView: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: theme.borderRadius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  headerInfo: {
    flex: 1,
  },
  title: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  section: {
    backgroundColor: theme.colors.white,
    marginTop: theme.spacing.md,
    padding: theme.spacing.lg,
  },
  sectionTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  filePickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.lg,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: theme.borderRadius.lg,
    backgroundColor: '#00C851' + '05',
    gap: theme.spacing.sm,
  },
  filePickerText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
  },
  fileInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing.sm,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.success + '10',
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing.xs,
  },
  fileInfoText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.success,
  },
  stepsContainer: {
    gap: theme.spacing.md,
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  stepNumber: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as any,
  },
  stepInfo: {
    flex: 1,
  },
  stepName: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    marginBottom: 2,
  },
  stepDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  progressCard: {
    marginTop: theme.spacing.lg,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    borderLeftWidth: 4,
  },
  progressHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.xs,
  },
  progressMessage: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
  },
  progressDetails: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.sm,
  },
  progressBar: {
    height: 4,
    backgroundColor: theme.colors.gray200,
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
  },
  statsContainer: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.lg,
  },
  statCard: {
    flex: 1,
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
  },
  statNumber: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  summaryCard: {
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  summaryValue: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  insightCard: {
    padding: theme.spacing.lg,
    backgroundColor: '#00C851' + '10',
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    borderColor: '#00C851' + '20',
  },
  insightHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.sm,
    gap: theme.spacing.xs,
  },
  insightTitle: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: '#00C851',
  },
  insightText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.xs,
    lineHeight: 18,
  },
  actionContainer: {
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  primaryButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.white,
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.white,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    gap: theme.spacing.sm,
  },
  secondaryButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
  },
  disabledButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.gray400,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
});
